import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import QRScanner from '@/components/QRScanner';
import { QrCode, Save } from 'lucide-react';
import BackButton from '@/components/BackButton';
import ScanButton from '@/components/ScanButton';
import { resolveScan, ScanResultType, ScanContext, validateForContext } from '@/utils/scanResolver';

const StandortScanPage = () => {
  const { qr } = useParams();
  const navigate = useNavigate();
  const { API, user } = useContext(AuthContext);
  const [standort, setStandort] = useState(null);
  const [slots, setSlots] = useState([]);
  const [availableAutomaten, setAvailableAutomaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [scanningForSlot, setScanningForSlot] = useState(null);

  useEffect(() => {
    fetchStandort();
  }, [qr]);

  const fetchStandort = async () => {
    try {
      const standortRes = await axios.get(`${API}/standorte/qr/${qr}`);
      setStandort(standortRes.data);

      // OPTIMIERT: Nutze spezialisierten Endpunkt für Automaten am Standort
      const locationAutomatenRes = await axios.get(`${API}/automaten/at-location/${standortRes.data.id}`);
      const locationAutomaten = locationAutomatenRes.data;

      // OPTIMIERT: Nutze spezialisierten Endpunkt für verfügbare Automaten
      // Reduziert Payload von ~200KB auf ~5KB bei 400 Automaten
      const availableRes = await axios.get(`${API}/automaten/available/for-assignment`);
      setAvailableAutomaten(availableRes.data.automaten);

      const initialSlots = [];
      for (let i = 1; i <= standortRes.data.slot_count; i++) {
        const automat = locationAutomaten.find((a) => a.current_slot_number === i);
        initialSlots.push({
          slot_number: i,
          action: 'ohne',
          previous_machine_id: automat?.id || null,
          previous_machine: automat || null,
          new_machine_id: null,
          new_machine: null,
          cash_collected: '',
          new_fill_level: '',
        });
      }
      setSlots(initialSlots);
    } catch (error) {
      toast.error('Fehler beim Laden des Standorts');
      navigate('/standorte');
    } finally {
      setLoading(false);
    }
  };

  const handleQRScan = async (qrValue) => {
    setShowScanner(false);
    if (scanningForSlot === null) return;

    // Normalisiere den gescannten Code (nur trim, KEIN parseInt - führende Nullen behalten!)
    const normalizedCode = qrValue.trim();
    console.log(`[StandortScan] Suche mit Code: '${normalizedCode}' (original: '${qrValue}')`);

    try {
      // NUTZE ZENTRALEN RESOLVER für konsistente Identifikation
      const result = await resolveScan({
        qrValue: normalizedCode,
        api: API,
        user,
        context: ScanContext.STANDORT_ZUWEISUNG
      });

      console.log(`[StandortScan] Resolver-Ergebnis:`, result);

      // Fall 1: Automat gefunden
      if (result.type === ScanResultType.AUTOMAT) {
        const automat = result.data;
        
        // Validierung mit Kontextregeln
        const validation = validateForContext(result, ScanContext.STANDORT_ZUWEISUNG);
        
        if (!validation.valid) {
          toast.error(validation.error);
          setScanningForSlot(null);
          return;
        }

        // Automat zum Slot hinzufügen
        const newSlots = [...slots];
        newSlots[scanningForSlot].new_machine_id = automat.id;
        newSlots[scanningForSlot].new_machine = automat;
        setSlots(newSlots);
        toast.success(`Automat ${automat.code} gescannt`);
      }
      // Fall 2: Standort gefunden (falscher Scan-Typ hier)
      else if (result.type === ScanResultType.STANDORT) {
        toast.error(`"${normalizedCode}" ist ein Standort-Code, kein Automat. Bitte einen Automaten-QR-Code scannen.`);
      }
      // Fall 3: Nicht gefunden
      else if (result.type === ScanResultType.UNKNOWN) {
        toast.error(`Code "${normalizedCode}" nicht gefunden. Existiert dieser Automat im System?`);
      }
      // Fall 4: Fehler (z.B. Berechtigung)
      else if (result.type === ScanResultType.ERROR) {
        toast.error(result.message);
      }
    } catch (error) {
      console.error(`[StandortScan] Fehler:`, error);
      toast.error('Fehler beim Verarbeiten des Scans');
    }
    setScanningForSlot(null);
  };

  const handleSubmit = async () => {
    try {
      const slotsData = slots.map((slot) => ({
        slot_number: slot.slot_number,
        action: slot.action,
        previous_machine_id: slot.previous_machine_id,
        new_machine_id: slot.new_machine_id,
        cash_collected: slot.cash_collected ? parseFloat(slot.cash_collected) : null,
        new_fill_level: slot.new_fill_level || null,
      }));

      const response = await axios.post(`${API}/besuche`, {
        location_id: standort.id,
        slots: slotsData,
      });

      await axios.put(`${API}/besuche/${response.data.besuch_id}/finish`);
      toast.success('Besuch erfolgreich gespeichert!');
      navigate(`/standorte/${standort.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="standort-scan-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <BackButton />
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Gehäuse bearbeiten
            </h1>
            <p className="text-slate-600">{standort?.name}</p>
          </div>
        </div>
        <Button onClick={handleSubmit} className="flex items-center space-x-2" data-testid="save-button">
          <Save size={20} />
          <span>Besuch beenden</span>
        </Button>
      </div>

      <div className="space-y-4">
        {slots.map((slot, index) => (
          <Card key={slot.slot_number} className="p-6" data-testid={`slot-${slot.slot_number}`}>
            <h3 className="text-lg font-bold mb-4">Slot {slot.slot_number}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Aktueller Automat</Label>
                <div className="p-3 bg-slate-50 rounded-lg text-sm">
                  {slot.previous_machine ? (
                    <div>
                      <p className="font-medium">{slot.previous_machine.code}</p>
                      <p className="text-slate-600">
                        {slot.previous_machine.machine_type} | {slot.previous_machine.coin_type} €
                      </p>
                    </div>
                  ) : (
                    <p className="text-slate-500">Kein Automat</p>
                  )}
                </div>
              </div>

              <div>
                <Label>Aktion</Label>
                <Select
                  value={slot.action}
                  onValueChange={(value) => {
                    const newSlots = [...slots];
                    newSlots[index].action = value;
                    setSlots(newSlots);
                  }}
                >
                  <SelectTrigger data-testid={`action-select-${slot.slot_number}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ohne">Ohne Änderung</SelectItem>
                    {slot.previous_machine ? (
                      <>
                        <SelectItem value="tausch">Tauschen</SelectItem>
                        <SelectItem value="leeren">Leeren</SelectItem>
                      </>
                    ) : (
                      <SelectItem value="neu">Neu bestücken</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {(slot.action === 'tausch' || slot.action === 'neu') && (
                <div className="col-span-2">
                  <Label>Neuer Automat</Label>
                  {slot.new_machine ? (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex justify-between items-center">
                      <div>
                        <p className="font-medium">{slot.new_machine.code}</p>
                        <p className="text-sm text-slate-600">
                          {slot.new_machine.machine_type} | {slot.new_machine.coin_type} €
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSlots = [...slots];
                          newSlots[index].new_machine_id = null;
                          newSlots[index].new_machine = null;
                          setSlots(newSlots);
                        }}
                      >
                        Ändern
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Dropdown for available automaten */}
                      {availableAutomaten.length > 0 ? (
                        <div>
                          <Label className="text-sm text-slate-600 mb-2">Verfügbare Automaten ({availableAutomaten.length})</Label>
                          <Select
                            onValueChange={(value) => {
                              const automat = availableAutomaten.find(a => a.id === value);
                              if (automat) {
                                const newSlots = [...slots];
                                newSlots[index].new_machine_id = automat.id;
                                newSlots[index].new_machine = automat;
                                setSlots(newSlots);
                                toast.success(`Automat ${automat.code} ausgewählt`);
                              }
                            }}
                            data-testid={`available-automaten-select-${slot.slot_number}`}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Automat auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableAutomaten.map((automat) => (
                                <SelectItem key={automat.id} value={automat.id}>
                                  {automat.code} - {automat.machine_type}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-700">
                            Keine befüllten Automaten (Lager VOLL) verfügbar
                          </p>
                        </div>
                      )}

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <span className="w-full border-t border-slate-200" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-slate-500">oder</span>
                        </div>
                      </div>

                      {/* QR Scanner Button */}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          setScanningForSlot(index);
                          setShowScanner(true);
                        }}
                        data-testid={`scan-new-machine-${slot.slot_number}`}
                      >
                        <QrCode size={20} className="mr-2" />
                        Per QR-Code scannen
                      </Button>

                      {/* Manual Input */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Code manuell eingeben"
                          onKeyPress={async (e) => {
                            if (e.key === 'Enter' && e.target.value.trim()) {
                              try {
                                const response = await axios.get(`${API}/automaten`);
                                const automat = response.data.find(
                                  a => a.code.toLowerCase() === e.target.value.trim().toLowerCase()
                                );
                                if (automat) {
                                  // Validierung: Nur "Lager VOLL" Automaten dürfen zugewiesen werden
                                  if (automat.status !== 'Lager VOLL') {
                                    toast.error(`Automat ${automat.code} hat Status "${automat.status}". Nur Automaten mit Status "Lager VOLL" können zugewiesen werden.`);
                                    return;
                                  }
                                  if (automat.current_location_id) {
                                    toast.error(`Automat ${automat.code} ist bereits an einem Standort zugewiesen.`);
                                    return;
                                  }
                                  if (automat.is_defect) {
                                    toast.error(`Automat ${automat.code} ist als defekt markiert.`);
                                    return;
                                  }
                                  const newSlots = [...slots];
                                  newSlots[index].new_machine_id = automat.id;
                                  newSlots[index].new_machine = automat;
                                  setSlots(newSlots);
                                  toast.success(`Automat ${automat.code} hinzugefügt`);
                                  e.target.value = '';
                                } else {
                                  toast.error('Automat nicht gefunden');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Suchen des Automaten');
                              }
                            }
                          }}
                          data-testid={`manual-input-machine-${slot.slot_number}`}
                        />
                        <Button
                          variant="outline"
                          onClick={async (e) => {
                            const input = e.target.closest('.flex').querySelector('input');
                            if (input && input.value.trim()) {
                              try {
                                const response = await axios.get(`${API}/automaten`);
                                const automat = response.data.find(
                                  a => a.code.toLowerCase() === input.value.trim().toLowerCase()
                                );
                                if (automat) {
                                  // Validierung: Nur "Lager VOLL" Automaten dürfen zugewiesen werden
                                  if (automat.status !== 'Lager VOLL') {
                                    toast.error(`Automat ${automat.code} hat Status "${automat.status}". Nur Automaten mit Status "Lager VOLL" können zugewiesen werden.`);
                                    return;
                                  }
                                  if (automat.current_location_id) {
                                    toast.error(`Automat ${automat.code} ist bereits an einem Standort zugewiesen.`);
                                    return;
                                  }
                                  if (automat.is_defect) {
                                    toast.error(`Automat ${automat.code} ist als defekt markiert.`);
                                    return;
                                  }
                                  const newSlots = [...slots];
                                  newSlots[index].new_machine_id = automat.id;
                                  newSlots[index].new_machine = automat;
                                  setSlots(newSlots);
                                  toast.success(`Automat ${automat.code} hinzugefügt`);
                                  input.value = '';
                                } else {
                                  toast.error('Automat nicht gefunden');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Suchen des Automaten');
                              }
                            }
                          }}
                          data-testid={`submit-manual-machine-${slot.slot_number}`}
                        >
                          OK
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {slot.action === 'leeren' && (
                <>
                  <div>
                    <Label>Geldbetrag (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={slot.cash_collected}
                      onChange={(e) => {
                        const newSlots = [...slots];
                        newSlots[index].cash_collected = e.target.value;
                        setSlots(newSlots);
                      }}
                      placeholder="0.00"
                      data-testid={`cash-input-${slot.slot_number}`}
                    />
                  </div>
                  <div>
                    <Label>Neuer Füllstand</Label>
                    <Select
                      value={slot.new_fill_level}
                      onValueChange={(value) => {
                        const newSlots = [...slots];
                        newSlots[index].new_fill_level = value;
                        setSlots(newSlots);
                      }}
                    >
                      <SelectTrigger data-testid={`fill-level-select-${slot.slot_number}`}>
                        <SelectValue placeholder="Wählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="leer">Leer</SelectItem>
                        <SelectItem value="viertel">1/4 voll</SelectItem>
                        <SelectItem value="halb">1/2 voll</SelectItem>
                        <SelectItem value="drei_viertel">3/4 voll</SelectItem>
                        <SelectItem value="voll">Voll</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </Card>
        ))}
      </div>

      {showScanner && (
        <QRScanner onScan={handleQRScan} onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
};

export default StandortScanPage;